import { Typography } from "components/typography";
import { useGetSignedAttachment } from "../hooks";
import { styled } from "styled-components";
import { DeleteAttachmentModal } from "../DeleteAttachmentModal";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "../messageAttachment.styles";
import { FilePreviewControls } from "./Controls";
import { isErrorWithStatus } from "api/helpers";

type Props = {
  type: "create" | "view";
  attachmentKey: string;
  onDelete?: (fileName: string) => void;
};

export const FilePreview = ({ attachmentKey, onDelete }: Props) => {
  const { t } = useTranslation();

  const { data, isImage, failureReason } =
    useGetSignedAttachment(attachmentKey);

  const [isOpen, setIsOpen] = useState(false);

  if (isErrorWithStatus(failureReason, 404)) {
    return (
      <Container>
        <ErrorMessage>{t("ATTACHMENT_NOT_FOUND")}</ErrorMessage>
      </Container>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Container>
        <>
          <Asset>
            {isImage && <Image src={data.url} alt={attachmentKey} />}
            {!isImage && (
              <Typography variant="body-x-small" color="GRAY_DARK">
                {t("NO_PREVIEW_AVAILABLE")}
              </Typography>
            )}
          </Asset>
          <FilePreviewControls
            url={data.url}
            fileName={data.originalFileName ?? "Unknown File"}
            setIsOpen={setIsOpen}
            disabled={!onDelete}
          />
        </>
      </Container>
      {onDelete && (
        <DeleteAttachmentModal
          open={isOpen}
          onClose={() => setIsOpen(false)}
          attachmentKey={attachmentKey}
          onSuccess={onDelete}
        />
      )}
    </>
  );
};

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  aspect-ratio: 16 / 9;
`;

const Asset = styled.div`
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  display: grid;
  width: 100%;
  &:only-of-type {
    grid-template-columns: 1fr;
  }
  grid-template-columns: 50% 50%;
  grid-template-rows: 1fr;
  gap: ${({ theme }) => theme.SPACING.SM};
`;
